import * as Sebrae from '@sebrae/react-core';
import React, { useState } from 'react';

import {
  BaSeButton,
  BaSeCheckbox,
  BaSeDatePicker,
  BaSeDialogContext,
  BaSeGrid,
  BaSeGridItem,
  BaSeHeading6,
  BaSeInput,
  BaSeMessage,
  BaSeOptionPicker,
  BaSeParagraph,
  BaSePopupButton,
  BaSeSmall1,
  BaSeTheme,
} from '@base/react';
import { useManageClassController } from '../../../../../../hooks/controllers/manage-class-controller';
import { useUfController } from '../../../../../../hooks/controllers/uf-controller';
import { useMessage } from '../../../../../../hooks/messages/useMessage';
import { useModule } from '../../../../../../hooks/module/module';
import { useRegionalService } from '../../../../../../hooks/services/regional-service';
import { formatarData } from '../../../../../../utils/formatters/dateMask';

import {
  ContainerConfirmation,
  ContentButtons,
} from './form-edit-class-romm-style';
import {
  AlignButtons,
  AlignInformations,
  AlignRow,
} from './form-new-class-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';
const COLOR_DESCRIPTION = '#394D60';
const COLOR_DESCRIPTION_DESTAQ = '#0000c4';

const formatCurrency = (val) => {
  if (!val) return '';
  const numericValue = val.toString().replace(/\D/g, '');
  const formattedValue = (Number(numericValue) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formattedValue;
};

const convertDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function FormEditClassRoom({
  classData,
  handleUpdateColumns,
  closeFormEditClass,
  nameTurma,
  classRoomOriginFoco
}) {

  const { handleError, handleSuccess } = useMessage();
  const { showModal } = React.useContext(BaSeDialogContext);
  const { hasRole } = Sebrae.useAuth();

  const { editClassRoomById } = useManageClassController();
  const { getUfs } = useUfController();

  const { moduleSelected } = useModule();
  const { returnRegionals } = useRegionalService();

  const [dtoEditClass, setDtoEditClass] = useState(classData);
  const [acept, setAcept] = React.useState(dtoEditClass.hidePortal || false);

  const [initialDate, setInitialDate] = React.useState(
    new Date(`${classData.beginDate}T00:00:00`),
  );
  const [finalDate, setFinalDate] = React.useState(
    new Date(`${classData.endDate}T00:00:00`),
  );
  const [regionalList, setRegionalList] = React.useState([]);
  const [regionalSelected, setRegionalSelected] = React.useState([]);

  const [listUfs, setListUfs] = React.useState([]);
  const [selectedUF, setSelectedUF] = React.useState([]);

  const [listCityByRegional, setListCityByRegional] = React.useState([]);
  const [selectedCITY, setSelectedCITY] = useState([]);

  const getRegionalsByUf = React.useCallback(
    (idUf) => {
      returnRegionals({ idUf, status: true }).then((response) => {
        let listaComRegional = [...response];

        if (classData.regional) {
          const jaExiste = listaComRegional.some(
            (regional) => regional.id === classData.regional.id,
          );
          if (!jaExiste) {
            //validação para não incrementar a uf default da regional em uma regional de outro estado
            if (classData.regional.uf.id === idUf) {
              listaComRegional = [...listaComRegional, classData.regional];
            }
          }
        }

        const responseFormated = listaComRegional.map((item) => ({
          ...item,
          label: item.name,
          name: item.name,
        }));

        setRegionalList(responseFormated);
      });
    },
    [returnRegionals, classData],
  );

  React.useEffect(() => {
    if (dtoEditClass.seminarValue !== '') {
      setDtoEditClass((prev) => {
        const isFormatted = prev.seminarValue.toString().includes(',');

        const rawValue = isFormatted
          ? prev.seminarValue
          : prev.seminarValue * 100;
        prev.seminarValue = formatCurrency(rawValue.toString());
        return prev;
      });
    }
  }, [dtoEditClass.seminarValue]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const response = await getUfs();
        setListUfs(response);

        if (cache.hasItem(cacheKeySebrae)) {
          if (cache.getItem(cacheKeySebrae) !== 'NA') {
            const ufGestorLogado = await response.find(
              (res) => res.sigla === cache.getItem(cacheKeySebrae),
            );
            if (ufGestorLogado) {
              setSelectedUF([ufGestorLogado]);
              getRegionalsByUf(ufGestorLogado.id, true);
            }
            return;
          }
          const ufTurma = await response.find(
            (res) => res.sigla === classData.city.uf.initials,
          );
          if (ufTurma) {
            setSelectedUF([ufTurma]);
          }
        }
      } catch (e) {
        setListUfs([]);
      }
    };
    load();
  }, [getUfs, hasRole, classData.city.uf.initials, getRegionalsByUf]);

  const handleChange = React.useCallback((name, value) => {
    setDtoEditClass((prevDto) => ({
      ...prevDto,
      [name]: value,
    }));
  }, []);

  const convertToNumber = (currencyString) => {
    const normalized = String(currencyString)
      .replace(/\./g, '')
      .replace(',', '.');
    return parseFloat(normalized);
  };

  const openDialogConfirmationClassRoom = React.useCallback(
    (newDto) => {
      return showModal({
        actions: [],
        size: 'small',
        title: `Editar turma: ${nameTurma}`,
        dismissible: true,
        isFluid: true,
        dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
        dismissibleIconType: 'tertiary',
        breakpointDelimiter: 'mobileLarge',
        content: ({ closeModal, emitEvent }) => (
          <ContainerConfirmation>
            <BaSeHeading6
              color={BaSeTheme.colors.feedbackInterface.atencao55}
              isBold={true}
            >
              Atenção!
            </BaSeHeading6>

            <BaSeParagraph isBold>
              {`Tem certeza que deseja editar a turma no ${moduleSelected?.name}?`}
            </BaSeParagraph>

            <div>
              <AlignRow>
                <BaSeParagraph>UF:</BaSeParagraph>
                <BaSeParagraph
                  color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                >{`${selectedUF[0].label}`}</BaSeParagraph>
              </AlignRow>
              <AlignRow>
                <BaSeParagraph>Regional:</BaSeParagraph>
                <BaSeParagraph
                  color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                >
                  {regionalSelected[0].label}
                </BaSeParagraph>
              </AlignRow>

              <AlignRow>
                <BaSeParagraph>Cidade:</BaSeParagraph>
                <BaSeParagraph
                  color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                >{`${selectedCITY[0]?.label}`}</BaSeParagraph>
              </AlignRow>
            </div>

            <div>
              <BaSeParagraph>
                As datas definidas de realização do seminário foram:
              </BaSeParagraph>
              <BaSeParagraph
                color={BaSeTheme.colors.institucionais.cinzaSebrae60}
              >{`Data de início: ${convertDate(initialDate)}`}</BaSeParagraph>
              <AlignRow>
                <BaSeParagraph
                  color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                >{`Data de término: ${convertDate(finalDate)}`}</BaSeParagraph>
              </AlignRow>
            </div>

            <AlignRow>
              <BaSeParagraph>Valor do seminário:</BaSeParagraph>
              <BaSeParagraph
                color={BaSeTheme.colors.institucionais.cinzaSebrae60}
              >{`R$ ${dtoEditClass.seminarValue}`}</BaSeParagraph>
            </AlignRow>
            <AlignRow>
              <BaSeParagraph>Ocultar turma no portal:</BaSeParagraph>
              <BaSeParagraph
                color={BaSeTheme.colors.institucionais.cinzaSebrae60}
              >
                {acept ? 'Sim' : 'Não'}
              </BaSeParagraph>
            </AlignRow>

            <ContentButtons>
              <BaSeButton
                color={BaSeTheme.colors.defaultColors.black}
                value="Confirmar"
                onClick={() => {
                  editClassRoomById(classData.id, newDto)
                    .then(() => {
                      closeFormEditClass();
                      closeModal();
                      handleUpdateColumns();
                      handleSuccess('Turma salva com sucesso!');
                    })
                    .catch((error) => {
                      console.error(error);
                      handleError(error.message ?? 'Erro ao salvar a turma.');
                    });
                }}
              />

              <BaSeButton
                color={BaSeTheme.colors.defaultColors.black}
                type="secondary"
                value="Revisar"
                onClick={() => {
                  closeModal();
                }}
              />
            </ContentButtons>
          </ContainerConfirmation>
        ),
        onChooseAction: (action) => {
          if (action === 'Confirmar') {
            editClassRoomById(classData.id, newDto)
              .then(() => {
                closeFormEditClass();
                handleUpdateColumns();
                handleSuccess('Turma salva com sucesso!');
              })
              .catch((error) => {
                console.error(error);
              });
          }
        },
      });
    },
    [
      showModal,
      nameTurma,
      handleError,
      moduleSelected,
      selectedUF,
      regionalSelected,
      selectedCITY,
      initialDate,
      finalDate,
      dtoEditClass.seminarValue,
      acept,
      editClassRoomById,
      classData.id,
      closeFormEditClass,
      handleUpdateColumns,
      handleSuccess,
    ],
  );

  const validate = () => {
    if (
      !dtoEditClass.beginDate ||
      !dtoEditClass.endDate ||
      !selectedUF.length ||
      !selectedCITY.length ||
      !dtoEditClass.seminarValue ||
      !initialDate ||
      !finalDate ||
      regionalSelected.length === 0
    ) {
      handleError('É necessário preencher todas as informações da turma!');
      return;
    }
    const newDto = {
      ...dtoEditClass,
      seminarValue: convertToNumber(dtoEditClass.seminarValue),
      hidePortal: acept,
      regionalId: regionalSelected[0].id,
    };
    openDialogConfirmationClassRoom(newDto);
  };
  const TODAY = new Date();
  const MORE_INITIAL_DATE = new Date(
    new Date(initialDate).getTime() + 86400000,
  );
  const message = (
    <BaSeSmall1 color={COLOR_DESCRIPTION}>
      Ao ser marcada essa opção, o sistema não apresentará a turma na lista de
      turmas para cadastro do candidato no portal. A turma poderá ter sua gestão
      realizada no gerenciamento de turma e será apresentada em tom{' '}
      <b style={{ color: COLOR_DESCRIPTION_DESTAQ }}>azul escuro</b> no board.
    </BaSeSmall1>
  );
  return (
    <>
      <BaSeGrid delimitedOnBreakpoint="desktop">
        {
          classRoomOriginFoco && (
            <BaSeGridItem size={12}>
              <BaSeMessage
                color="attention"
                title="Essa turma foi criada no FOCO."
                message=" Os campos bloqueados não podem ser editados. Caso necessário, você pode marcar a opção 'Ocultar turma no portal'."
              />
            </BaSeGridItem>
          )
        }
        <BaSeGridItem size={12}>
          {cache.getItem(cacheKeySebrae) !== 'NA' || classRoomOriginFoco ? (
            <BaSeOptionPicker
              label="UF do seminário:"
              values={listUfs}
              isDisabled
              emptyValueLabel="UF"
              maxOptionsSelected={1}
              initialSelectedValuesId={selectedUF.map((val) => val.id)}
              onChange={async (values) => {
                if (values.length === 0) return;

                getRegionalsByUf(JSON.parse(values.map((val) => val.id)));
                setSelectedUF(values);
                if (classData.regional) {
                  if (
                    classData.regional.uf.id ===
                    JSON.parse(values.map((val) => val.id))
                  ) {
                    setRegionalSelected([
                      {
                        id: classData.regional.id,
                        label: classData.regional.name,
                      },
                    ]);
                    const citiesFormated = classData.regional.cities.map(
                      (c) => {
                        return {
                          ...c,
                          label: c.name,
                        };
                      },
                    );
                    setListCityByRegional(citiesFormated);
                    setSelectedCITY([{ id: classData.regional.cities[0].id }]);
                  }
                }
              }}
              extraActions={[
                {
                  action: () => {
                    setSelectedUF([]);
                    setSelectedCITY([]);
                  },
                  label: 'Limpar',
                },
              ]}
            />
          ) : (
            <BaSeOptionPicker
              label="UF do seminário:"
              values={listUfs}
              emptyValueLabel="UF"
              maxOptionsSelected={1}
              initialSelectedValuesId={selectedUF.map((val) => val.id)}
              onChange={async (values) => {
                if (values.length === 0) return;

                getRegionalsByUf(
                  JSON.parse(values.map((val) => val.id)),
                  false,
                );
                if (classData.regional) {
                  if (
                    classData.regional.uf.id ===
                    JSON.parse(values.map((val) => val.id))
                  ) {
                    setRegionalSelected([
                      {
                        id: classData.regional.id,
                        label: classData.regional.name,
                      },
                    ]);
                    const citiesFormated = classData.regional.cities.map(
                      (c) => {
                        return {
                          ...c,
                          label: c.name,
                        };
                      },
                    );
                    setListCityByRegional(citiesFormated);
                    setSelectedCITY([{ id: classData.regional.cities[0].id }]);
                  } else {
                    setRegionalSelected([]);
                    setSelectedCITY([]);
                  }
                }

                setSelectedUF(values);
              }}
              extraActions={[
                {
                  action: () => {
                    setSelectedUF([]);
                    setSelectedCITY([]);
                  },
                  label: 'Limpar',
                },
              ]}
            />
          )}
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Regional:"
            values={regionalList}
            isDisabled={classRoomOriginFoco}
            emptyValueLabel="Regional"
            maxOptionsSelected={1}
            initialSelectedValuesId={regionalSelected.map((val) => val.id)}
            onChange={(values) => {
              if (values.length === 0) return;

              setRegionalSelected(values);
              const valuesFormated = values[0].cities.map((item) => ({
                ...item,
                label: item.name,
              }));
              setListCityByRegional(valuesFormated);
              setSelectedCITY([]);
            }}
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Cidade:"
            values={listCityByRegional}
            isDisabled={classRoomOriginFoco}
            emptyValueLabel="Cidade"
            maxOptionsSelected={1}
            initialSelectedValuesId={selectedCITY.map((val) => val.id)}
            onChange={(values) => {
              if (values.length === 0) return;
              handleChange('cityId', values[0].id);
              handleChange('moduleId', moduleSelected.id && moduleSelected.id);
              setSelectedCITY(values);
            }}
            extraActions={[
              {
                action: () => {
                  setSelectedCITY([]);
                },
                label: 'Limpar',
              },
            ]}
          />
        </BaSeGridItem>

        <BaSeGridItem size={6}>
          <BaSeDatePicker
            isDisabled={classRoomOriginFoco}
            onStartDateChange={(date) => {
              setInitialDate(date);
              handleChange('beginDate', formatarData(date));
            }}
            startDate={initialDate}
            label="Data início:"
            placeholder="00/00/0000"
            width="300px"
            minDate={TODAY}
            maxDate={
              finalDate ? new Date(finalDate.getTime() - 86400000) : null
            }
          />
        </BaSeGridItem>

        <BaSeGridItem size={6}>
          <BaSeDatePicker
            isDisabled={classRoomOriginFoco}
            onStartDateChange={(date) => {
              setFinalDate(date);
              handleChange('endDate', formatarData(date));
            }}
            startDate={finalDate}
            label="Data fim:"
            placeholder="00/00/0000"
            minDate={MORE_INITIAL_DATE}
            width="300px"
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeInput
            isDisabled={classRoomOriginFoco}
            color="default"
            inputMode="text"
            label="Valor do seminário:"
            placeholder="R$"
            prefix="R$"
            size="medium"
            subLabel=""
            typeInput="text"
            value={dtoEditClass.seminarValue}
            width="100%"
            onChange={(value) => {
              handleChange('seminarValue', formatCurrency(value));
            }}
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <AlignInformations>
            <BaSeCheckbox
              align="top-left"
              color="default"
              label="Ocultar turma no portal"
              value={acept}
              checked={acept}
              onChange={() => setAcept((prev) => !prev)}
              shape="square"
              size="medium"
            />
            <BaSePopupButton
              alertSize={270}
              iconProps={{
                color: undefined,
                name: 'exclamation-circle',
                size: 34,
              }}
              message={message}
              title="Title"
              type="tooltip"
            />
          </AlignInformations>
        </BaSeGridItem>
      </BaSeGrid>

      <AlignButtons>
        <BaSeButton onClick={validate} type="primary" value="Salvar" />
        <BaSeButton
          onClick={closeFormEditClass}
          type="secondary"
          value="Cancelar"
        />
      </AlignButtons>
    </>
  );
}
export default FormEditClassRoom;
